import React, { useState } from 'react';
import { FileText, ChevronDown, ChevronUp, AlertTriangle, FileCheck, HelpCircle } from 'lucide-react';

/**
 * Компонент с информацией о юридических аспектах криптоинвестиций в России
 */
const RussianLegalInfo = () => {
  const [expandedSection, setExpandedSection] = useState(null);
  
  const toggleSection = (section) => {
    if (expandedSection === section) {
      setExpandedSection(null);
    } else {
      setExpandedSection(section);
    }
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <FileText className="w-6 h-6 text-teal-500" />
        Правовой статус криптовалют в России
      </h2>
      
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-6">
        <div className="flex gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-amber-800 dark:text-amber-300 mb-1">Важное замечание</h3>
            <p className="text-sm text-amber-700 dark:text-amber-400">
              Информация представлена по состоянию на апрель 2025 года. Законодательство в сфере криптовалют 
              в России продолжает развиваться. Для принятия юридически значимых решений рекомендуем 
              проконсультироваться с квалифицированным юристом.
            </p>
          </div>
        </div>
      </div>
      
      {/* Законодательная база */}
      <div className="mb-4 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <button 
          className="flex items-center justify-between w-full p-4 bg-gray-50 dark:bg-gray-700/50 text-left"
          onClick={() => toggleSection('legislation')}
        >
          <div className="flex items-center gap-2">
            <FileCheck className="w-5 h-5 text-teal-500" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Текущее законодательство</h3>
          </div>
          {expandedSection === 'legislation' ? 
            <ChevronUp className="w-5 h-5 text-gray-500 dark:text-gray-400" /> : 
            <ChevronDown className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          }
        </button>
        
        {expandedSection === 'legislation' && (
          <div className="p-4 bg-white dark:bg-gray-800">
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <p>
                Основным нормативным актом, регулирующим отношения в сфере цифровых активов в России, 
                является Федеральный закон от 31.07.2020 N 259-ФЗ «О цифровых финансовых активах, 
                цифровой валюте и о внесении изменений в отдельные законодательные акты Российской Федерации».
              </p>
              
              <h4>Ключевые положения:</h4>
              <ul>
                <li><strong>Определение криптовалюты</strong> - закон определяет понятие «цифровая валюта»</li>
                <li><strong>Запрет на оплату</strong> - криптовалюта не может быть использована как средство платежа за товары и услуги на территории РФ</li>
                <li><strong>Право владения</strong> - владение и операции с криптовалютой разрешены</li>
                <li><strong>Декларирование</strong> - владельцы криптовалют обязаны декларировать свои активы и уплачивать с них налоги</li>
                <li><strong>Судебная защита</strong> - владельцы цифровых валют получают судебную защиту только при условии декларирования владения цифровой валютой и операций с ней</li>
              </ul>
              
              <p>
                В марте 2022 года вступил в силу Закон о налогообложении цифровой валюты, который 
                установил порядок налогообложения доходов от операций с цифровой валютой.
              </p>
              
              <p>
                В начале 2023 года была принята «Концепция законодательного регулирования оборота 
                цифровых валют», а в 2024 году внесены дополнительные изменения, легализующие майнинг 
                криптовалют.
              </p>
            </div>
          </div>
        )}
      </div>
      
      {/* Налогообложение */}
      <div className="mb-4 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <button 
          className="flex items-center justify-between w-full p-4 bg-gray-50 dark:bg-gray-700/50 text-left"
          onClick={() => toggleSection('taxation')}
        >
          <div className="flex items-center gap-2">
            <FileCheck className="w-5 h-5 text-teal-500" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Налогообложение криптовалют</h3>
          </div>
          {expandedSection === 'taxation' ? 
            <ChevronUp className="w-5 h-5 text-gray-500 dark:text-gray-400" /> : 
            <ChevronDown className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          }
        </button>
        
        {expandedSection === 'taxation' && (
          <div className="p-4 bg-white dark:bg-gray-800">
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <p>
                Согласно действующему законодательству, доходы от операций с криптовалютами облагаются 
                налогом на доходы физических лиц (НДФЛ) по стандартной ставке 13%.
              </p>
              
              <h4>Налогообложение криптовалютных операций:</h4>
              <ul>
                <li><strong>Объект налогообложения</strong> - прибыль, полученная от продажи криптовалюты (разница между доходами от продажи и расходами на приобретение)</li>
                <li><strong>Ставка налога</strong> - 13% для резидентов РФ</li>
                <li><strong>Декларирование</strong> - обязательное ежегодное декларирование в рамках налоговой декларации 3-НДФЛ</li>
                <li><strong>Доказательства расходов</strong> - для уменьшения налоговой базы необходимо документально подтвердить расходы на приобретение криптовалюты</li>
                <li><strong>Самостоятельный расчет</strong> - налогоплательщик самостоятельно рассчитывает налоговую базу и уплачивает налог</li>
              </ul>
              
              <p>
                Важно отметить, что в 2025 году в России введены специальные правила учета и расчета налоговой 
                базы для операций с цифровой валютой. Расходы на приобретение криптовалюты учитываются 
                по методу FIFO (First In, First Out) - первая купленная монета считается первой проданной.
              </p>
              
              <p className="text-amber-700 font-medium">
                Обратите внимание: неуплата налогов с доходов от операций с криптовалютами может повлечь 
                не только штрафы, но и уголовную ответственность при крупных суммах.
              </p>
            </div>
          </div>
        )}
      </div>
      
      {/* Регулирование бирж */}
      <div className="mb-4 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <button 
          className="flex items-center justify-between w-full p-4 bg-gray-50 dark:bg-gray-700/50 text-left"
          onClick={() => toggleSection('exchanges')}
        >
          <div className="flex items-center gap-2">
            <FileCheck className="w-5 h-5 text-teal-500" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Регулирование криптобирж</h3>
          </div>
          {expandedSection === 'exchanges' ? 
            <ChevronUp className="w-5 h-5 text-gray-500 dark:text-gray-400" /> : 
            <ChevronDown className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          }
        </button>
        
        {expandedSection === 'exchanges' && (
          <div className="p-4 bg-white dark:bg-gray-800">
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <p>
                В России регулирование деятельности криптовалютных бирж имеет свои особенности:
              </p>
              
              <ul>
                <li>
                  <strong>Отсутствие полноценного лицензирования</strong> - на текущий момент в России 
                  отсутствует официальная процедура лицензирования криптовалютных бирж
                </li>
                <li>
                  <strong>Ограниченный доступ к иностранным биржам</strong> - некоторые крупные международные 
                  криптобиржи либо ограничили доступ для российских пользователей, либо требуют 
                  использования VPN-сервисов
                </li>
                <li>
                  <strong>Экспериментальные правовые режимы</strong> - начиная с 2024 года, в некоторых 
                  регионах России заработали пилотные проекты по лицензированию отдельных криптовалютных 
                  платформ
                </li>
                <li>
                  <strong>Проблемы с банковским обслуживанием</strong> - российские банки с осторожностью 
                  относятся к операциям, связанным с криптовалютными биржами
                </li>
                <li>
                  <strong>P2P-платформы</strong> - в России активно развиваются P2P-платформы для обмена 
                  криптовалюты, позволяющие совершать операции напрямую между пользователями
                </li>
              </ul>
              
              <p>
                В 2025 году Центральный Банк России ведет работу над созданием правил для регистрации 
                операторов обмена цифровых валют, что может привести к появлению официально 
                зарегистрированных в России криптовалютных бирж.
              </p>
            </div>
          </div>
        )}
      </div>
      
      {/* Часто задаваемые вопросы */}
      <div className="mb-4 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <button 
          className="flex items-center justify-between w-full p-4 bg-gray-50 dark:bg-gray-700/50 text-left"
          onClick={() => toggleSection('faq')}
        >
          <div className="flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-teal-500" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Часто задаваемые вопросы</h3>
          </div>
          {expandedSection === 'faq' ? 
            <ChevronUp className="w-5 h-5 text-gray-500 dark:text-gray-400" /> : 
            <ChevronDown className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          }
        </button>
        
        {expandedSection === 'faq' && (
          <div className="p-4 bg-white dark:bg-gray-800">
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">Законно ли в России владеть криптовалютой?</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Да, владение криптовалютой в России законно. Однако использование криптовалюты в качестве 
                  средства платежа на территории РФ запрещено.
                </p>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">Нужно ли платить налоги с доходов от криптовалют?</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Да, доходы от операций с криптовалютами подлежат налогообложению. Ставка НДФЛ составляет 13% 
                  для резидентов РФ. Налогом облагается прибыль - разница между доходами от продажи и расходами 
                  на приобретение.
                </p>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">Как правильно декларировать криптовалютные доходы?</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Доходы от операций с криптовалютами необходимо отражать в налоговой декларации 3-НДФЛ. 
                  Декларация подается ежегодно не позднее 30 апреля года, следующего за годом получения дохода. 
                  Уплата налога должна быть произведена до 15 июля.
                </p>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">Могу ли я использовать иностранные криптобиржи?</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Законодательство не запрещает российским гражданам использовать иностранные криптобиржи. Однако 
                  некоторые биржи могут ограничивать доступ для пользователей из России или требовать дополнительные 
                  процедуры верификации. Использование VPN для обхода ограничений может противоречить правилам 
                  использования конкретных сервисов.
                </p>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">Что грозит за неуплату налогов с криптовалютных операций?</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Неуплата налогов с доходов от операций с криптовалютами может повлечь наложение штрафа в размере 
                  20% от неуплаченной суммы налога (при непреднамеренной неуплате) или 40% (при умышленной неуплате). 
                  При крупных суммах неуплаченного налога (от 2,7 млн рублей) возможна уголовная ответственность 
                  по ст. 198 УК РФ.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      
      <div className="text-sm text-gray-600 dark:text-gray-400 italic mt-4">
        Представленная информация не является юридической консультацией. Для получения профессиональной 
        правовой помощи по вопросам инвестирования в криптовалюты рекомендуем обратиться к юристу, 
        специализирующемуся на цифровом праве.
      </div>
    </div>
  );
};

export default RussianLegalInfo;